import type {
  AllColorThemes,
  ColorThemes,
  MeasurementSystems,
  ReducedMotionTypes,
  SUPPORTED_LANGUAGES,
} from "../app/constants";
import type { RouteType } from "../directions/types";
import type {
  MaplibreLightsConfig,
  MaplibreRendererConfig,
  MaplibreShadowConfig,
} from "../map/layers/maplibre/ThreeJS/types";

export const SearchResultsSearchBy = ["name", "info", "both"] as const;
export const SearchResultsSortBy = ["distance", "match", "both"] as const;
export const PoiCategoryFilterInterface = ["list", "grid"] as const;

interface I18nState {
  language: SupportedLanguage | undefined;
  selectableLanguages: SupportedLanguage[];
  measurementSystem: MeasurementSystems;
}

export enum CartographyToShow {
  BASEMAP = "BASEMAP",
  TILES = "TILES",
  GEOJSON = "GEOJSON",
  FLOORPLAN = "FLOORPLAN",
}

export enum BorderThemes {
  default = "Default",
  none = "None",
}

export enum PrefetchState {
  NotStarted = "NotStarted",
  CurrentBuilding = "CurrentBuilding",
  Finish = "Finish",
}

export enum PerformanceMode {
  high = "high",
  low = "low",
}

export interface FontMap {
  url: string;
  lang: SupportedLanguage;
}

interface InformativeMessages {
  showRouteDelayWarningToReachTheFlight: boolean;
}

export const FontSizeListArray = [
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "xxl",
  "xxxl",
] as const;

export const FontSizeEnum = Object.fromEntries(
  FontSizeListArray.map((value) => [value.toUpperCase(), value]),
) as { [K in Uppercase<FontSizeItem>]: string };

export type FontSizeItem = (typeof FontSizeListArray)[number];

export enum MapControlItemEnum {
  Compass = "compass",
  ZoomButtons = "zoomButtons",
  ShareMap = "shareMap",
  Fullscreen = "fullscreen",
}

export type MapControlItem = `${MapControlItemEnum}`;

export enum UserSettingsPanelItem {
  LANGUAGE = "language",
  THEME = "theme",
  HIGH_CONTRAST = "high_contrast",
  FONT_SIZE = "font_size",
  REDUCED_MOTION = "reduced_motion",
  GEOFENCE_POPUPS = "geofence_popups",
  SPEAK_INDICATIONS = "speak_indications",
  REDUCED_MOBILITY = "reduced_mobility",
  AVOID_UNEVEN_SURFACES = "avoid_uneven_surfaces",
  AVOID_STEEP_PATHS = "avoid_steep_paths",
  AVOID_BRIGHT_LIGHT_AREAS = "avoid_bright_light_areas",
  AVOID_NOISY_AREAS = "avoid_noisy_areas",
  AVOID_CROWDED_AREAS = "avoid_crowded_areas",
}

export const MAP_CONTROL_ITEMS: MapControlItem[] =
  Object.values(MapControlItemEnum);

export enum TTSEngine {
  WEB = "web",
  MOBILE = "mobile",
}

export const DEFAULT_SHARE_PROFILE = "__DEFAULT_SHARE_PROFILE__";

export interface InitialViewState {
  building_id: number | null;
  floor_id: number | null;
  zoom: number | null;
  bearing: number | null;
  pitch: number | null;
  fixedPoi: number | null;
  latitude: number | null;
  longitude: number | null;
}

interface WidgetVisibilityState {
  search_bar: boolean;
  top_search_bar: boolean;
  poi_list: boolean;
  poi_categories: boolean;
  poi_subcategories: boolean;
  navigation_indications: boolean;
  navigation_info: boolean;
  destination_reached_confirmation: boolean;
  map_control: boolean;
  map_control_items: MapControlItem[];
  exploration_control: boolean;
  poi_panel: boolean;
  poi_names: boolean;
  poi_names_navigation: boolean;
  location_control: boolean;
  building_selector: boolean;
  lock_floor_selector: boolean;
  floor_selector: boolean;
  directions_panel: boolean;
  route_type_selector: boolean;
  directions_shortcut: boolean;
  language_control: boolean;
  routeinfo_tooltip: boolean;
  zoom_buttons_mobile: boolean;
  share_buttons: boolean;
  kiosk_qr_code: boolean;
}

export interface StylesMapLabelsState {
  color: string;
  fontSize: number;
  outline: {
    color: string;
    width: number;
  };
  backgroundColor?: string;
  offset: number;
}

export interface PoiBackgroundState {
  enabled: boolean;
  color: string;
  iconScaleFactor: number;
  iconType: "CIRCLE" | "PIN" | "SQUARE-ICON";
  pinHeight: number;
}

export interface StylesPoisState {
  iconScale: number;
  fadeOffRoutePois: boolean;
  outline: {
    color_selected: string;
  };
  iconBackground: PoiBackgroundState;
  dots: {
    enabled: boolean;
    threshold: number;
    color: string;
  };
}

interface StylesMapState {
  labels: StylesMapLabelsState;
  pois: StylesPoisState;
}

interface CustomizableThemeState {
  brand_color: string;
  primary_color: string;
  secondary_color: string;
  primary_font_color: string;
  secondary_font_color: string;
  icon_color: string;
  background_color: string;
  hover_color: string;
}

interface StylesThemeState {
  colorTheme: AllColorThemes;
  border_theme: string;
  light_mode: CustomizableThemeState;
  dark_mode: CustomizableThemeState;
  warning_color: string;
  positioning_icon_color: string;
  positioning_dot_color: string;
  positioning_outdoor_dot_color: string;
  positioning_accuracy_color: string;
  fonts: FontMap[];
}

interface FavoritePoisState {
  iconOutlineColor: string;
}

interface StylesState {
  path_color: string;
  path_unselected_color: string;
  path_animation_color: string;
  path_arrow_color: string;
  path: {
    dots_gap: number;
    dots_size: number;
    show_step_arrow: boolean;
    step_fitting_zoom: number;
  };
  map: StylesMapState;
  theme: StylesThemeState;
  map_font: string | null;
  fixedPoi: FixedPoiStyle;
}

interface PoiState {
  spacingFactor: number;
  zoomLevelToShowText: number | null;
}

interface CameraState {
  //Max, min zoom as configured by parameter or profile
  maxZoom: number | null;
  minZoom: number | null;
  //Max, min zoom taking into account config and building dimensions
  maxCurrentBuildingZoom: number | null;
  minCurrentBuildingZoom: number | null;
  lockToBuilding: boolean | null;
  allowCameraAutoFitToRoute: boolean | null;
  minZoomToCenter: number | null;
  maxZoomToCenter: number | null;
  paddingHorizontal: number | null;
  paddingVertical: number | null;
}

export interface LocationQualityThreslholds {
  accuracyThreshold: number;
  maxJumpAllowedThreshold: number;
  minimumWalkThreshold: number;
  locationsWithoutBearingThreshold: number;
}

export interface LocationUpdateThesholds {
  distance: number;
  bearing: number;
  accuracy: number;
  animationDuration: number;
  minUpdateIntervalMs: number;
}

export enum PrefetchMode {
  NONE = "none",
  MINIMAL = "minimal",
  ALL = "all",
}

export enum ActionTriggerType {
  HAS_TAG = "indication_has_tag", // navigation has tag on any momment
  CURRENT_TAG = "indication_current_tag", // current step on navigation has tag
  GEOFENCE_ENTER = "geofence_enter",
}

export interface ActionTriggerOptions {
  tag: string;
  onlyOnce: boolean;
  cooldown: number;
}

export enum ActionType {
  MODAL = "modal",
  // NOTIFICATION = "notification",
  // POSTMESSAGE = "postmessage",
  // EVENT = "event",
}
export interface ModalContent {
  title: { [key in SupportedLanguage]?: string };
  description: { [key in SupportedLanguage]?: string };
}
export interface Action {
  id: string;
  enabled: boolean;
  triggerOptions: ActionTriggerOptions;
  trigger: ActionTriggerType;
  action: ActionType;
  actionOptions: { modal?: ModalContent };
}

export enum ActionEventTypes {
  routeUpdated = "routeUpdated",
  navigationStarted = "navigationStarted",
  navigationUpdated = "navigationUpdated",
  navigationStopped = "navigationStopped",
}

interface InternalState {
  disableNavigationEvents: boolean;
  crawlersAllowIndex: boolean;
  embed: boolean;
  injectSimulation: boolean;
  dashboardEmbed: boolean;
  debug: boolean;
  profile: boolean;
  simulator: boolean;
  appName: string;
  deviceId: string | null;
  showPois: boolean;
  showGeofences: boolean;
  showPaths: boolean;
  showModels: boolean;
  prefetchData: PrefetchMode;
  prefetch: {
    enableTilePrefetch: boolean;
    minTileZoom: number;
    maxTileZoom: number;
  };
  performanceMode: PerformanceMode;
  locationQualityThresholds: LocationQualityThreslholds;
  locationUpdateThresholds: LocationUpdateThesholds;
  refreshRate: number | null;
  refreshDate: {
    hour: string | null;
    repeat: boolean;
  };
  limitedRefresh: boolean;
  tryRefreshOnFirstLoad: boolean;
  autoResetView: {
    enabled: boolean;
    idleTime: number;
  };
  tts: {
    rate: number;
    pitch: number;
    volume: number;
    engine: TTSEngine;
  };
}

export enum MapAttributionsTypes {
  situm = "situm",
  none = "none",
}

export enum CustomUi {
  adr = "adr",
}

export interface FixedPoiStyle {
  outlineColor: string;
  showName: boolean;
  showIcon: boolean;
  showLabel: boolean;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  textOutlineColor: string;
  fontSize: number;
}

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export interface ConfigState {
  auth: {
    api_key: string;
    jwt: string | null;
    production_api_key: string | null;
    domain: string;
  };
  i18n: I18nState;
  ui: {
    rtl: boolean;
    whiteLabel: boolean;
    header_image: string | null;
    map_attribution: MapAttributionsTypes;
    poi_surname: string;
    poi_category_filter_interface: (typeof PoiCategoryFilterInterface)[number];
    poiCategoriesOrder: number[];
    user_settings_panel_items: UserSettingsPanelItem[];
    widget_visibility: WidgetVisibilityState;
    reducedMotion: ReducedMotionTypes;
    search_results: {
      sort_by: (typeof SearchResultsSortBy)[number];
      fuzzy_threshold: number;
      search_by: (typeof SearchResultsSearchBy)[number];
    };
    fontSize: FontSizeItem;
  };
  map: {
    interactive: boolean;
    initialView: InitialViewState;
    useTiles: boolean; //TODO: Maintain for compatibility until all profiles are migrated
    cartographyToShow: CartographyToShow[];
    basemap: {
      active: boolean; //TODO:  Maintain for compatibility until all profiles are migrated
      darkMode: boolean;
      custom_style_url: string | null;
      custom_style_high_contrast_dark_url: string | null;
      preset: string | null;
      upload_style: string | null;
    };
    newPoiLayer: boolean;
    newIMDFLayer: boolean;
    newLocationBehaviour: boolean;
    newLocationLayer: boolean;
    newFloorplanLayer: boolean;
    newModelsLayers: boolean;
    newIconTextLayers: boolean;
    newRouteLabelLayer: boolean;
    newRouteIndicationLayer: boolean;
    lights: MaplibreLightsConfig;
    shadows: MaplibreShadowConfig;
    renderer: MaplibreRendererConfig;
  };
  navigation: {
    distanceToGoalThreshold?: number;
    outsideRouteThreshold?: number;
    outsideRouteThresholdOnIncorrectFloor?: number;
    distanceToIgnoreIndication?: number;
    distanceToFloorChangeThreshold?: number;
    distanceToChangeIndicationThreshold?: number;
    indicationsInterval?: number;
    timeToFirstIndication?: number;
    roundIndicationsStep?: number;
    timeToIgnoreUnexpectedFloorChanges?: number;
    timeToUpdateNextIndication?: number;
    distanceToReadIndicationTheshold?: number;
  };
  actions: {
    actions: Action[] | null;
  };
  directions: {
    defaultRouteType: RouteType;
    excludedTags: string[] | null;
    includedTags: string[] | null;
    thresholdPoiDistances: number;
    radiusPoiIndication: number;
    turnThreshold: number;
    turnBackThreshold: number;
    distanceToDiscriminateLookToTurn: number;
    slightTurnThreshold: number;
    longRouteWarning: {
      distanceThreshold: number | null;
      timeThreshold: number | null;
      categoriesIds: number[];
      maxDistanceToNearPois: number;
    };
  };
  favoritePois: FavoritePoisState;
  flight_information: {
    enabled: boolean;
    category_id: number | null;
    polling_interval_seconds: number;
  };
  feature_flags: {
    custom_ui: string | null;
    find_my_car_active: boolean;
    allow_reselecting_route_target: boolean;
    ar_integration: boolean;
    kiosk_mode: boolean;
    ticket_selector: boolean;
    flat_earth: boolean;
    favorites: boolean;
    speak_indications: boolean;
    user_settings_button: boolean;
    share_location: boolean;
    directions_autoplay_steps_timer: number;
    browser_positioning: boolean;
    browser_positioning_distance: number;
    simplified_navigation_ui: boolean;
    feedback_form: boolean;
  };
  styles: StylesState;
  poi: PoiState;
  camera: CameraState;
  internal: InternalState;
  informativeMessages: InformativeMessages;
  share: {
    baseUrl: string;
    profile: string;
  };
}
